import { getNotificationWebSocketTicket } from '../api/messages';

/**
 * Централизованный сервис для управления WebSocket подключением к уведомлениям
 */
class NotificationWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000;
    this.isConnecting = false;
    this.listeners = new Map();
    this.listenerId = 0;
    this.connectionStatus = 'disconnected'; // 'websocket', 'api', 'disconnected'
    this.statusListeners = new Set();
    this.reconnectTimer = null;
    this.activeConsumers = 0;
    /** Подавляет api/disconnected при намеренном reconnect/connect до open или финального сбоя */
    this.suppressDiagnostics = false;
    this.intentionalClose = false;
    /** Инкремент при disconnect/reconnect — отменяет in-flight connect после await ticket */
    this.connectGeneration = 0;
  }

  /** Отменить текущую/ожидающую попытку подключения */
  bumpConnectGeneration() {
    this.connectGeneration += 1;
    this.isConnecting = false;
  }

  isConnectAttemptCurrent(generation) {
    return generation === this.connectGeneration;
  }

  /**
   * Подписка на изменения статуса подключения
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);
    // Сразу вызываем callback с текущим статусом
    callback(this.connectionStatus);
    
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Уведомление всех подписчиков об изменении статуса
   */
  notifyStatusChange(status) {
    this.connectionStatus = status;
    this.statusListeners.forEach(callback => callback(status));
  }

  /**
   * Подключение к WebSocket
   */
  async connect() {
    const existing = this.ws;
    if (
      this.isConnecting ||
      (existing &&
        (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING))
    ) {
      return;
    }

    if (!localStorage.getItem('token')) {
      this.suppressDiagnostics = false;
      this.notifyStatusChange('disconnected');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const generation = this.connectGeneration;
    this.isConnecting = true;
    this.notifyStatusChange('connecting');

    try {
      const ticketResponse = await getNotificationWebSocketTicket();

      if (!this.isConnectAttemptCurrent(generation)) {
        return;
      }

      const ticket =
        ticketResponse?.ticket ??
        ticketResponse?.websocket_ticket ??
        ticketResponse?.ws_ticket;
      if (!ticket || typeof ticket !== 'string') {
        throw new Error('WebSocket ticket is missing');
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = `${protocol}//${host}/api/ws?ticket=${encodeURIComponent(ticket)}`;
      const socket = new WebSocket(wsUrl);
      this.ws = socket;

      socket.onopen = () => {
        if (!this.isConnectAttemptCurrent(generation) || this.ws !== socket) {
          return;
        }
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.suppressDiagnostics = false;
        this.notifyStatusChange('websocket');
      };

      socket.onmessage = (event) => {
        if (!this.isConnectAttemptCurrent(generation) || this.ws !== socket) {
          return;
        }
        try {
          if (typeof event.data !== 'string' || event.data.trim() === '') {
            return;
          }
          const data = JSON.parse(event.data);

          this.listeners.forEach(({ callback, type }) => {
            if (!type || data.type === type) {
              callback(data);
            }
          });
        } catch (error) {
          void error;
        }
      };

      socket.onclose = (event) => {
        if (!this.isConnectAttemptCurrent(generation) || this.ws !== socket) {
          return;
        }
        this.isConnecting = false;
        this.ws = null;

        if (this.intentionalClose) {
          this.intentionalClose = false;
          if (this.suppressDiagnostics) {
            return;
          }
        }

        if (this.activeConsumers <= 0) {
          this.suppressDiagnostics = false;
          this.notifyStatusChange('disconnected');
          return;
        }

        if (event.code === 1000 || event.code === 1001) {
          if (!this.suppressDiagnostics) {
            this.notifyStatusChange('disconnected');
          }
          return;
        }

        if (!this.suppressDiagnostics) {
          this.notifyStatusChange('api');
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
        } else {
          this.suppressDiagnostics = false;
          this.notifyStatusChange('disconnected');
        }
      };

      socket.onerror = (error) => {
        void error;
        if (!this.isConnectAttemptCurrent(generation) || this.ws !== socket) {
          return;
        }
        this.isConnecting = false;
        if (this.suppressDiagnostics || this.intentionalClose) {
          return;
        }
        this.notifyStatusChange('api');
      };
    } catch (_error) {
      if (!this.isConnectAttemptCurrent(generation)) {
        return;
      }
      this.isConnecting = false;
      this.suppressDiagnostics = false;
      this.notifyStatusChange('api');
    } finally {
      if (
        this.isConnectAttemptCurrent(generation) &&
        this.isConnecting &&
        (!this.ws || this.ws.readyState === WebSocket.CLOSED)
      ) {
        this.isConnecting = false;
      }
    }
  }

  /**
   * Отключение от WebSocket
   */
  disconnect() {
    this.bumpConnectGeneration();
    this.suppressDiagnostics = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.ws;
    if (socket) {
      this.intentionalClose = true;
      socket.close(1000, 'disconnect');
    }
    this.isConnecting = false;
    if (!socket) {
      this.notifyStatusChange('disconnected');
    }
  }

  acquireConnection() {
    this.activeConsumers += 1;
    void this.connect();
    return () => this.releaseConnection();
  }

  releaseConnection() {
    this.activeConsumers = Math.max(0, this.activeConsumers - 1);
    if (this.activeConsumers === 0) {
      this.disconnect();
    }
  }

  /**
   * Подписка на сообщения определенного типа
   */
  subscribe(type, callback) {
    this.listenerId += 1;
    const key = `listener_${this.listenerId}`;
    this.listeners.set(key, { type, callback });
    
    return () => {
      this.listeners.delete(key);
    };
  }

  /**
   * Подписка на все сообщения
   */
  subscribeAll(callback) {
    return this.subscribe(null, callback);
  }

  /**
   * Отправка сообщения через WebSocket
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  /**
   * Отправка ping сообщения
   */
  ping() {
    return this.send({ type: 'ping' });
  }

  /**
   * Получение текущего статуса подключения
   */
  getStatus() {
    return this.connectionStatus;
  }

  /**
   * Проверка активности подключения
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Подключение по действию пользователя (кнопка «Подключить»)
   */
  connectManually() {
    this.suppressDiagnostics = true;
    void this.connect();
  }

  /**
   * Принудительное переподключение
   */
  reconnect() {
    this.bumpConnectGeneration();
    this.suppressDiagnostics = true;
    this.reconnectAttempts = 0;
    this.notifyStatusChange('connecting');

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.ws;
    if (socket) {
      this.intentionalClose = true;
      socket.close(1000, 'reconnect');
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, 1000);
  }

  isDiagnosticsPaused() {
    return this.suppressDiagnostics || this.connectionStatus === 'connecting';
  }
}

// Создаем единственный экземпляр сервиса
const notificationWebSocketService = new NotificationWebSocketService();

export default notificationWebSocketService; 